// src/pages/GenerarTicket.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaCheckCircle, FaSearch, FaArrowLeft, FaTint } from 'react-icons/fa';
import { db, storage, functions } from '../firebase';
import { collection, collectionGroup, where, doc, writeBatch, query, orderBy, getDocs, onSnapshot, serverTimestamp, getDoc, updateDoc, getDocFromServer } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { registerMovement } from '../utils/registerMovement';
import { calcularEstadoMoroso, calcularAdeudoConInteres } from '../utils/morosoUtils';
// import { calcularAdeudoVisibleDesdeObjeto } from '../utils/calcularAdeudoVisible'; // Ya no se usa - mostramos adeudo real siempre
import ModalRegistroPago from '../components/ModalRegistroPago';
import ConfirmationModal from '../components/ConfirmationModal';
import { pushToSyncQueue } from '../utils/offlineSyncQueue';
import './GenerarTicket.css';


const pickFirstString = (values = []) => {
    for (const value of values) {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed.length > 0) return trimmed;
        }
    }
    return '';
};

const getResidentName = (casa = {}) => pickFirstString([
    casa.nombre_residente,
    casa.nombre,
    casa.nombreTitular,
    casa.titular,
    casa.propietario,
    casa.arrendador,
    casa.nombrePropietario,
    casa.nombreCompleto,
]);

const GenerarTicket = () => {
    // --- ESTADOS ---
    const navigate = useNavigate();
    const location = useLocation();
    const [casas, setCasas] = useState([]);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [ingresos, setIngresos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState(() => {
        return location.state?.search || '';
    });
    const [financialConfig, setFinancialConfig] = useState({});
    const [confirmationModal, setConfirmationModal] = useState({
        open: false,
        title: '',
        message: '',
        type: 'info',
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCasa, setSelectedCasa] = useState(null);

    const { userData, currentUser } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;

    // Helper para obtener el adeudo VISIBLE (respeta período de gracia)
    // Durante meses y días de gracia: muestra solo adeudoCapital
    // Después de meses y días de gracia: muestra adeudo total
    const getAdeudoAmount = (casa = {}) => {
        const adeudoReal = Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
        const adeudoCapital = Number(casa?.adeudoCapital ?? 0);
        const adeudoIntereses = Number(casa?.adeudoIntereses ?? 0);
        const mesesAdeudo = Number(casa?.mesesAdeudo ?? 0);

        // Si no hay adeudo, retornar 0
        if (adeudoReal <= 0) {
            return 0;
        }

        // Obtener mesesPermitidosGracia guardado o configuración actual
        const mesesPermitidosGraciaGuardado = casa?.mesesPermitidosGracia;
        const mesesPermitidosConfig = Number(financialConfig?.mesesPermitidos ?? 1);
        const mesesPermitidos = (mesesPermitidosGraciaGuardado !== undefined && mesesPermitidosGraciaGuardado !== null)
            ? Number(mesesPermitidosGraciaGuardado)
            : mesesPermitidosConfig;

        // Obtener día de sanciones y día actual
        const sancionesInicioDia = Number(financialConfig?.sancionesInicioDia ?? financialConfig?.corteDia ?? 1);
        const diaActual = new Date().getDate();

        // CORREGIDO: Verificar meses Y días de gracia para determinar qué mostrar
        // Durante meses de gracia: mostrar solo adeudoCapital
        // Solo mostrar adeudo real (total) cuando se cumplieron AMBOS: meses Y días de gracia
        // IMPORTANTE: Si mesesAdeudo >= mesesPermitidos Y diaActual >= sancionesInicioDia, ya cumplió ambos
        const dentroDeMesesGracia = mesesAdeudo < mesesPermitidos;
        const dentroDeDiasGracia = diaActual < sancionesInicioDia;
        const cumplioMesesYGracia = mesesAdeudo >= mesesPermitidos && diaActual >= sancionesInicioDia;

        // Log para debugging (solo para casas específicas o cuando hay problema)
        if (casa.numero === '123' || casa.numero === 123 || casa.numero === '333' || casa.numero === 'A-333') {
            console.log('🔍 [GenerarTicket] Calculando adeudo VISIBLE para casa:', {
                numero: casa.numero,
                adeudoReal: adeudoReal,
                adeudoCapital: adeudoCapital,
                adeudoIntereses: adeudoIntereses,
                mesesAdeudo: mesesAdeudo,
                mesesPermitidos: mesesPermitidos,
                mesesPermitidosGraciaGuardado: mesesPermitidosGraciaGuardado,
                diaActual: diaActual,
                sancionesInicioDia: sancionesInicioDia,
                dentroDeMesesGracia: dentroDeMesesGracia,
                dentroDeDiasGracia: dentroDeDiasGracia,
                cumplioMesesYGracia: cumplioMesesYGracia,
                adeudoVisible: cumplioMesesYGracia ? adeudoReal : (dentroDeMesesGracia ? adeudoCapital : adeudoCapital),
            });
        }

        // Si cumplió AMBOS (meses Y días de gracia), mostrar adeudo total
        if (cumplioMesesYGracia) {
            return adeudoReal;
        }

        // Si está dentro de meses de gracia (mesesAdeudo < mesesPermitidos), mostrar solo capital
        if (dentroDeMesesGracia) {
            return adeudoCapital;
        }

        // Caso especial: mesesAdeudo = mesesPermitidos pero aún no pasó el día de sanciones
        // Aún mostrar solo capital hasta que pase el día de sanciones
        if (mesesAdeudo === mesesPermitidos && diaActual < sancionesInicioDia) {
            return adeudoCapital;
        }

        // Si superó meses de gracia pero aún no pasó el día de sanciones, mostrar solo capital
        return adeudoCapital;
    };

    // Helper para obtener el total de meses adelantados de una casa
    // Solo cuenta meses de gracia de ingresos válidos (cuota de mantenimiento + descuento/pronto pago)
    const getTotalMesesAdelantado = (casaId) => {
        return ingresos
            .filter(ing => {
                // Filtrar solo ingresos de esta casa con meses de gracia
                if (ing.casaId !== casaId || !ing.mesesPagadosAdelantado || ing.mesesPagadosAdelantado <= 0) {
                    return false;
                }

                // Validar que el concepto sea válido para meses de gracia
                const conceptoLower = String(ing.concepto || '').toLowerCase();
                const esRecargosIntereses = conceptoLower.includes('recargo') || conceptoLower.includes('interes');
                const esCuotaMantenimiento = conceptoLower.includes('cuota') && conceptoLower.includes('mantenimiento');
                const esDescuentoProntoPago = conceptoLower.includes('descuento') || conceptoLower.includes('pronto pago');

                // Solo contar si:
                // - NO es recargos/intereses
                // - Es cuota de mantenimiento
                // - Y tiene descuento/pronto pago (en el concepto o en descuentoPorcentaje)
                const tieneDescuento = esDescuentoProntoPago || (ing.descuentoPorcentaje && Number(ing.descuentoPorcentaje || 0) > 0);

                return !esRecargosIntereses && esCuotaMantenimiento && tieneDescuento;
            })
            .reduce((sum, ing) => sum + (ing.mesesPagadosAdelantado || 0), 0);
    };

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const [mapAguaPendiente, setMapAguaPendiente] = useState({});
    const [mapMultasPendientes, setMapMultasPendientes] = useState({});
    const [mapExtrasPendientes, setMapExtrasPendientes] = useState({});

    // --- useEffect: listener en tiempo real de casas ---
    useEffect(() => {
        if (!fraccionamientoId) {
            setLoading(false);
            return;
        }

        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const q = query(casasRef, orderBy('numero'));
        console.log('GenerarTicket: suscribiendo listener de casas para', fraccionamientoId);
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const casasList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Filtrar perfiles no residentes (caseta/guardia) igual que antes
            const casasFiltradas = casasList.filter(casa => {
                if (!casa.perfil) return true;
                const p = String(casa.perfil).toLowerCase().trim();
                if (p === 'caseta' || p === 'guardia') return false;
                return true;
            });
            setCasas(casasFiltradas);
            setLoading(false);
            console.log('GenerarTicket: actualización en tiempo real, casas:', casasFiltradas.length);
        }, (error) => {
            console.error('GenerarTicket: error en listener de casas', error);
            setLoading(false);
        });
        return () => {
            console.log('GenerarTicket: cancelando listener de casas');
            unsubscribe();
        };
    }, [fraccionamientoId]);

    // Escuchar todas las multas administrativas no pagadas para mapear los adeudos por casaId y casaNumero
    useEffect(() => {
        if (!fraccionamientoId) {
            setMapMultasPendientes({});
            return;
        }
        const multasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas');
        const unsubscribe = onSnapshot(multasRef, (snapshot) => {
            const mapMultas = {};
            snapshot.docs.forEach(docMulta => {
                const dataMulta = docMulta.data();
                const pagada = dataMulta.pagada === true;
                if (!pagada) {
                    const cId = String(dataMulta.casaId || '').trim().toUpperCase();
                    const cNum = String(dataMulta.casaNumero || dataMulta.numeroCasa || '').trim().toUpperCase();
                    const monto = Number(dataMulta.monto || 0);
                    if (cId) mapMultas[cId] = (mapMultas[cId] || 0) + monto;
                    if (cNum) mapMultas[cNum] = (mapMultas[cNum] || 0) + monto;
                }
            });
            setMapMultasPendientes(mapMultas);
        }, (err) => {
            console.warn('GenerarTicket: error escuchando multasAdministrativas', err);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Escuchar todos los meses pendientes (cuotas extraordinarias) para mapear los adeudos por casaId y casaNumero
    useEffect(() => {
        if (!fraccionamientoId) {
            setMapExtrasPendientes({});
            setMapAguaPendiente({});
            return;
        }
        const q = query(
            collectionGroup(db, 'mesesPendientes'),
            where('fraccionamientoId', '==', String(fraccionamientoId))
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const mapExtras = {};
            const mapAguaFromMeses = {};
            snapshot.docs.forEach(docMes => {
                const data = docMes.data() || {};
                const mesId = docMes.id;
                const est = String(data.estado || '').toLowerCase();
                const esPagada = est === 'pagado_completo' || est === 'pagado_totalmente' || est === 'pagado';
                const esExtraordinaria = mesId.startsWith('EXT-') || data.tipo === 'cuota_extraordinaria';
                const esAguaDoc = mesId.startsWith('AGUA-') || data.tipo === 'adeudo_agua';
                
                if (!esPagada) {
                    const casaId = String(data.casaId || docMes.ref.parent?.parent?.id || '').trim().toUpperCase();
                    const casaNum = String(data.casaNumero || data.numeroCasa || '').trim().toUpperCase();
                    const monto = Number(data.montoPendiente ?? data.monto ?? 0);
                    
                    if (esExtraordinaria) {
                        if (casaId) mapExtras[casaId] = (mapExtras[casaId] || 0) + monto;
                        if (casaNum) mapExtras[casaNum] = (mapExtras[casaNum] || 0) + monto;
                    }
                    if (esAguaDoc) {
                        if (casaId) mapAguaFromMeses[casaId] = (mapAguaFromMeses[casaId] || 0) + monto;
                        if (casaNum) mapAguaFromMeses[casaNum] = (mapAguaFromMeses[casaNum] || 0) + monto;
                    }
                }
            });
            setMapExtrasPendientes(mapExtras);

            setMapAguaPendiente(prev => ({
                ...prev,
                ...mapAguaFromMeses
            }));
        }, (err) => {
            console.warn('GenerarTicket: error escuchando mesesPendientes', err);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    useEffect(() => {
        if (!fraccionamientoId) return;
        const consumosAguaRef = collection(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable');
        const unsubscribe = onSnapshot(consumosAguaRef, (snapshot) => {
            const mapAdeudo = {};
            snapshot.docs.forEach(docAgua => {
                const dataAgua = docAgua.data();
                const desglose = dataAgua.desgloseCasas || [];
                desglose.forEach(item => {
                    const est = String(item.estadoPago || item.estado || '').toUpperCase();
                    const isPendiente = item.pagado === false || (item.pagado !== true && est !== 'PAGADO');
                    if (isPendiente) {
                        const cId = String(item.casaId || '').trim().toUpperCase();
                        const cNum = String(item.casaNumero || '').trim().toUpperCase();
                        const monto = Number(item.cargoTotal || item.montoPendiente || item.monto || 0);
                        if (cId) {
                            mapAdeudo[cId] = (mapAdeudo[cId] || 0) + monto;
                        }
                        if (cNum) {
                            mapAdeudo[cNum] = (mapAdeudo[cNum] || 0) + monto;
                        }
                    }
                });
            });
            setMapAguaPendiente(prev => ({ ...mapAdeudo, ...prev }));
        }, (err) => {
            console.warn('GenerarTicket: error escuchando consumosAguaPotable', err);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // --- useEffect: listener en tiempo real de ingresos para meses adelantados ---
    useEffect(() => {
        if (!fraccionamientoId) {
            return;
        }

        const ingresosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos');
        const q = query(ingresosRef, orderBy('fecha', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const ingresosList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setIngresos(ingresosList);
        }, (error) => {
            console.error('GenerarTicket: error en listener de ingresos', error);
        });
        return () => unsubscribe();
    }, [fraccionamientoId]);

    // --- useEffect: listener de configuración financiera para período de gracia ---
    useEffect(() => {
        if (!fraccionamientoId) {
            return;
        }

        // RUTA CORRECTA: leer configuracionFinanciera como campo del documento fraccionamiento
        const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
        console.log('GenerarTicket: escuchando configuración financiera para gracia en:', fraccionamientoId);
        const unsubscribe = onSnapshot(fraccRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                const config = data.configuracionFinanciera || {};
                const configLoaded = {
                    // CAMPO CORRECTO: interesesMesesPermitidosDeuda (meses permitidos de mora)
                    mesesPermitidos: config?.interesesMesesPermitidosDeuda ?? config?.mesesPermitidos ?? config?.meses_permitidos ?? 1,
                    cuotaMensual: config?.cuotaMensual ?? config?.montoMensual ?? config?.cuota_mensual ?? 0,
                    tipoInteres: (config?.tipoInteres || config?.interesTipo || 'fixed'),
                    sancionesInicioDia: config?.sancionesInicioDia ?? config?.corteDia ?? 1,
                };
                setFinancialConfig(configLoaded);
                console.log('✅ GenerarTicket: configuración cargada ->', configLoaded);
                console.log(`🧮 GenerarTicket: tipoInteres activo: ${configLoaded.tipoInteres}`);
            } else {
                console.log('⚠️ GenerarTicket: documento fraccionamiento no existe');
                setFinancialConfig({ mesesPermitidos: 1, cuotaMensual: 0 });
            }
        }, (error) => {
            console.error('❌ GenerarTicket: error escuchando configuración financiera', error);
        });

        return () => {
            console.log('GenerarTicket: cancelando escucha de configuración financiera');
            unsubscribe();
        };
    }, [fraccionamientoId]);

    const fetchCasas = async () => {
        setLoading(true);
        try {
            // Obtener todas las casas
            const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
            const q = query(casasRef, orderBy('numero'));
            const querySnapshot = await getDocs(q);
            const casasList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtrar casas: excluir las que tienen perfil "caseta"
            // Solo incluir casas con perfil "propietario", "arrendador" o sin perfil definido
            const casasFiltradasPorPerfil = casasList.filter(casa => {
                // Si no tiene perfil, incluirla (puede ser residente sin perfil asignado)
                if (!casa.perfil) {
                    console.log(`Casa ${casa.numero}: Sin perfil - INCLUIDA`);
                    return true;
                }

                // Normalizar el perfil a minúsculas para comparación
                const perfilLower = casa.perfil.toLowerCase().trim();

                // Excluir si el perfil es "caseta" o "guardia"
                if (perfilLower === 'caseta' || perfilLower === 'guardia') {
                    console.log(`Casa ${casa.numero}: Perfil "${casa.perfil}" - EXCLUIDA`);
                    return false;
                }

                // Incluir si el perfil es "propietario" o "arrendador"
                if (perfilLower === 'propietario' || perfilLower === 'arrendador') {
                    console.log(`Casa ${casa.numero}: Perfil "${casa.perfil}" - INCLUIDA`);
                    return true;
                }

                // Si tiene otro perfil no reconocido, incluir por defecto
                console.log(`Casa ${casa.numero}: Perfil "${casa.perfil}" (no reconocido) - INCLUIDA`);
                return true;
            });

            setCasas(casasFiltradasPorPerfil);
        } catch (error) {
            console.error('GenerarTicket: Error fetching casas', error);
            setCasas([]);
        } finally {
            setLoading(false);
        }
    };

    // --- FUNCIONES ---
    const computeFolioIndex = async (fraccId, ingresoId) => {
        try {
            const q = query(collection(db, 'fraccionamientos', fraccId, 'ingresos'), orderBy('fecha', 'desc'));
            const snap = await getDocs(q);
            const ids = snap.docs.map(d => d.id);
            const pos = ids.indexOf(ingresoId);
            if (pos === -1) return 0;
            return snap.size - 1 - pos; // igual que en ListaIngresos
        } catch (err) {
            console.error('GenerarTicket: computeFolioIndex error', err);
            return 0;
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);
    };

    const handleOpenModal = (casa) => {
        setSelectedCasa(casa);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedCasa(null);
    };

    const handleConfirmPago = async (datosPago, options = {}) => {
        if (!selectedCasa || !fraccionamientoId) return;

        const pagos = Array.isArray(datosPago) ? datosPago : (datosPago?.pagos || []);
        const totalMonto = datosPago?.totalMonto ?? options?.totalMonto ?? 0;
        const totalDescuento = datosPago?.totalDescuento ?? options?.totalDescuento ?? 0;
        const montoFinal = datosPago?.montoFinal ?? options?.montoFinal ?? 0;
        const folioBase = datosPago?.folioBase ?? options?.folioBase;
        const folioSerie = datosPago?.folioSerie ?? options?.folioSerie;

        console.log('GenerarTicket: handleConfirmPago recibió:', { folioBase, folioSerie, pagosLength: pagos?.length });

        // Validar que pagos es un array
        if (!Array.isArray(pagos) || pagos.length === 0) {
            console.error('handleConfirmPago: pagos no válido', datosPago);
            setConfirmationModal({
                open: true,
                title: 'Error',
                message: 'Error: No hay pagos para registrar.',
                type: 'error',
            });
            return;
        }

        try {
            const batch = writeBatch(db);
            const ingresoRefs = []; // Guardar referencias para enviar correos después

            // NOTA: Ya NO actualizamos el adeudo aquí manualmente
            // La Cloud Function onIngresoCreated se encargará de actualizar
            // el saldo automáticamente cuando se registre cada ingreso

            // Operación: Crear 1 ÚNICO registro de ingreso unificado para TODAS las partidas
            let downloadURL = null;
            const primerPagoConComprobante = pagos.find(p => p.comprobante);
            if (primerPagoConComprobante) {
                try {
                    const storageRef = ref(storage, `comprobantes/${fraccionamientoId}/${selectedCasa.id}_${Date.now()}_${primerPagoConComprobante.comprobante.name}`);
                    const snapshot = await uploadBytes(storageRef, primerPagoConComprobante.comprobante);
                    downloadURL = await getDownloadURL(snapshot.ref);
                } catch (uploadError) {
                    console.error('Error subiendo comprobante:', uploadError);
                }
            }

            const ingresoRef = doc(collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos'));

            const folioSerieSafe = (folioSerie || 'R').replace(/[^A-Za-z]/g, '') || 'R';
            const folioConsecutivo = (folioBase !== null && folioBase !== undefined && !isNaN(folioBase))
                ? Number(folioBase)
                : null;
            const folioString = folioConsecutivo ? `${folioSerieSafe}${String(folioConsecutivo).padStart(6, '0')}` : null;

            const primerPago = pagos[0] || {};
            const conceptosResumen = pagos.map(p => p.concepto).filter(Boolean).join(', ');
            const conceptoFinal = pagos.length > 1 ? `Multi-Concepto: ${conceptosResumen}` : (primerPago.concepto || `Pago - Casa ${selectedCasa.numero}`);

            const obsLista = pagos.map(p => (p.observaciones || '').trim()).filter(Boolean);
            const obsGeneralRoot = (datosPago?.observaciones || options?.observaciones || '').trim();

            const bloquesNotas = [];
            obsLista.forEach(nota => {
                if (!bloquesNotas.includes(nota)) {
                    bloquesNotas.push(nota);
                }
            });

            if (obsGeneralRoot && !bloquesNotas.some(b => b.includes(obsGeneralRoot))) {
                bloquesNotas.push(obsGeneralRoot);
            }

            const observacionesFinales = bloquesNotas.join('\n');

            const totalMontoCalculado = totalMonto || pagos.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
            const totalDescuentoCalculado = totalDescuento || pagos.reduce((sum, p) => sum + (parseFloat(p.descuento) || 0), 0);
            const totalNetoCalculado = montoFinal || pagos.reduce((sum, p) => sum + (parseFloat(p.montoNeto) || 0), 0);

            const cleanPagos = pagos.map(p => {
                const copy = { ...p };
                delete copy.comprobante; // Eliminar objeto File no serializable
                return copy;
            });

            const singleIngresoData = {
                fraccionamientoId,
                monto: parseFloat(totalMontoCalculado || 0),
                descuento: parseFloat(totalDescuentoCalculado || 0),
                descuentoPorcentaje: 0,
                montoNeto: parseFloat(totalNetoCalculado || 0),
                mesesPagadosAdelantado: pagos.reduce((sum, p) => sum + (p.mesesPagadosAdelantado || 0), 0),
                concepto: conceptoFinal,
                casaId: selectedCasa.id,
                casaNumero: selectedCasa.numero,
                fecha: serverTimestamp(),
                timestamp: serverTimestamp(),
                fechaPagoOriginal: primerPago.fechaPago || null,
                metodoPago: primerPago.metodoPago || '',
                referencia: primerPago.referencia || '',
                observaciones: observacionesFinales,
                comprobanteURL: downloadURL || null,
                multaAdministrativa: (pagos.find(p => p.multaAdministrativa)?.multaAdministrativa) || primerPago.multaAdministrativa || null,
                pagos: cleanPagos, // <--- Arreglo unificado limpio sin objetos File
                esMultiConcepto: pagos.length > 1,
                ...(folioConsecutivo ? { folioConsecutivo, folioSerie: folioSerieSafe, folio: folioString } : {}),
            };

            // Sanitizar objeto para eliminar cualquier propiedad con valor 'undefined'
            const sanitizeObject = (obj) => JSON.parse(JSON.stringify(obj, (k, v) => v === undefined ? null : v));
            const firestoreIngresoPayload = {
                ...sanitizeObject(singleIngresoData),
                fecha: serverTimestamp(),
                timestamp: serverTimestamp()
            };

            ingresoRefs.push({ ref: ingresoRef, pago: firestoreIngresoPayload, downloadURL });
            batch.set(ingresoRef, firestoreIngresoPayload);

            // Ejecutar todas las operaciones a la vez
            await batch.commit();

            // Procesar actualizaciones asíncronas de agua potable si aplica
            for (const pago of pagos) {
                const esAgua = String(pago.concepto || '').toLowerCase().includes('agua') || String(pago.concepto || '').toLowerCase().includes('cea');
                if (esAgua && pago.periodoAguaId && selectedCasa?.id) {
                    const cId = selectedCasa.id;

                    // Si se consumió saldo a favor, restarlo de la casa de forma persistente
                    if (pago.saldoFavorConsumido > 0) {
                        try {
                            const casaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', cId);
                            const casaDocSnap = await getDoc(casaDocRef);
                            if (casaDocSnap.exists()) {
                                const currentSF = Number(casaDocSnap.data().saldoFavor || 0);
                                const newSF = Math.max(0, Math.round((currentSF - Number(pago.saldoFavorConsumido)) * 100) / 100);
                                await updateDoc(casaDocRef, { saldoFavor: newSF });
                                console.log(`✓ Se descontaron $${pago.saldoFavorConsumido} de saldo a favor de la casa. Nuevo SF: $${newSF}`);
                            }
                        } catch (errSF) {
                            console.error('Error al descontar saldo a favor de la casa:', errSF);
                        }
                    }

                    try {
                        const consumoRef = doc(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable', pago.periodoAguaId);
                        const consumoSnap = await getDoc(consumoRef);
                        if (consumoSnap.exists()) {
                            const dataConsumo = consumoSnap.data();
                            const desglose = dataConsumo.desgloseCasas || [];
                            const indexCasa = desglose.findIndex(c => String(c.casaId || '').trim().toUpperCase() === String(cId).trim().toUpperCase());
                            
                            // 1. Marcar el desglose como PAGADO en consumosAguaPotable
                            if (indexCasa >= 0) {
                                desglose[indexCasa].pagado = true;
                                desglose[indexCasa].estadoPago = 'PAGADO';
                                desglose[indexCasa].montoPendiente = 0;
                                await updateDoc(consumoRef, { desgloseCasas: desglose });
                                console.log('✓ Se actualizó el período de agua a PAGADO en consumosAguaPotable');

                                // 1.1 Descontar el cargo de agua del adeudo total de la casa en Firestore
                                const cargoAgua = Number(desglose[indexCasa]?.cargoTotal || desglose[indexCasa]?.monto || desglose[indexCasa]?.montoPendiente || 0);
                                if (cargoAgua > 0) {
                                    try {
                                        const casaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', cId);
                                        const casaSnap = await getDoc(casaDocRef);
                                        if (casaSnap.exists()) {
                                            const currentAdeudo = Number(casaSnap.data().adeudo || 0);
                                            const currentCapital = Number(casaSnap.data().adeudoCapital || 0);
                                            const newAdeudo = Math.max(0, Math.round((currentAdeudo - cargoAgua) * 100) / 100);
                                            const newCapital = Math.max(0, Math.round((currentCapital - cargoAgua) * 100) / 100);
                                            const idNorm = String(cId).trim().toUpperCase();
                                            const numNorm = String(casaSnap.data().numero || casaSnap.data().numeroCasa || '').trim().toUpperCase();
                                            const aguaPendienteActual = (mapAguaPendiente[idNorm] || 0) || (mapAguaPendiente[numNorm] || 0);
                                            const tieneMasAgua = aguaPendienteActual > cargoAgua;

                                            await updateDoc(casaDocRef, {
                                                adeudo: newAdeudo,
                                                adeudoCapital: newCapital,
                                                tieneAdeudoAguaVariable: tieneMasAgua,
                                                adeudoAgua: tieneMasAgua ? Math.max(0, aguaPendienteActual - cargoAgua) : 0,
                                                montoAguaPendiente: tieneMasAgua ? Math.max(0, aguaPendienteActual - cargoAgua) : 0
                                            });
                                            console.log(`✓ [GenerarTicket] Se descontaron $${cargoAgua} del adeudo de la casa. Nuevo adeudo: $${newAdeudo}, tieneAdeudoAguaVariable: ${tieneMasAgua}`);
                                        }
                                    } catch (errCasaUpdate) {
                                        console.error('Error al actualizar adeudo de la casa por agua:', errCasaUpdate);
                                    }
                                }
                            }

                            // 2. Marcar el documento de mesesPendientes de la casa como PAGADO
                            const mesLectura = dataConsumo.mesLectura || 'ACTUAL';
                            const anoLectura = dataConsumo.anoLectura || new Date().getFullYear();
                            const pendienteId = `AGUA-${cId}-${mesLectura}-${anoLectura}`;
                            const pendienteRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', cId, 'mesesPendientes', pendienteId);
                            
                            try {
                                const pendSnap = await getDoc(pendienteRef);
                                if (pendSnap.exists()) {
                                    const cargoAgua = Number(desglose[indexCasa]?.cargoTotal || desglose[indexCasa]?.monto || desglose[indexCasa]?.montoPendiente || 284.00);
                                    await updateDoc(pendienteRef, {
                                        estado: 'pagado',
                                        estadoResidente: 'AL CORRIENTE',
                                        cuotaMensual: cargoAgua > 0 ? cargoAgua : 284.00,
                                        montoPagado: cargoAgua > 0 ? cargoAgua : 284.00,
                                        montoPendiente: 0,
                                        colorBandera: 'verde'
                                    });
                                    console.log('✓ Se actualizó mesesPendientes a pagado con montoPagado para agua:', pendienteId);
                                }
                            } catch (errPend) {
                                console.warn('No se pudo encontrar o actualizar mesesPendientes para agua:', errPend);
                            }
                        }
                    } catch (errConsumo) {
                        console.error('Error al procesar el pago de agua variable:', errConsumo);
                    }
                }
            }

            // Procesar actualizaciones directas de sanciones administrativas pagadas
            for (const pago of pagos) {
                const cLower = String(pago.concepto || '').toLowerCase();
                const esSancion = cLower.includes('sancion') || cLower.includes('sansion') || cLower.includes('multa');
                if (esSancion && selectedCasa?.id) {
                    try {
                        const multasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas');
                        let multaDocRef = null;
                        let multaTargetData = null;

                        // 1. Si tenemos el docId directo de la sanción
                        if (pago.multaAdminDocId) {
                            const directSnap = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas', pago.multaAdminDocId));
                            if (directSnap.exists() && !directSnap.data().pagada) {
                                multaDocRef = directSnap.ref;
                                multaTargetData = directSnap.data();
                            }
                        }

                        // 2. Si no, buscar por casaId o casaNumero
                        if (!multaDocRef) {
                            let qMultas = query(multasRef, where('casaId', '==', selectedCasa.id));
                            let snapMultas = await getDocs(qMultas);
                            if (snapMultas.empty && selectedCasa.numero) {
                                qMultas = query(multasRef, where('casaNumero', '==', String(selectedCasa.numero).trim()));
                                snapMultas = await getDocs(qMultas);
                            }
                            const multasActivas = snapMultas.docs.filter(d => !d.data().pagada);
                            const multaTarget = multasActivas.find(d => {
                                const data = d.data();
                                if (pago.multaAdminId && (String(d.id) === String(pago.multaAdminId) || String(data.multaId) === String(pago.multaAdminId))) return true;
                                if (pago.multaAdminDesc && data.multaDescripcion === pago.multaAdminDesc) return true;
                                if (pago.multaAdministrativa && data.multaDescripcion === pago.multaAdministrativa) return true;
                                return false;
                            }) || multasActivas[0];

                            if (multaTarget) {
                                multaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas', multaTarget.id);
                                multaTargetData = multaTarget.data();
                            }
                        }

                        if (multaDocRef) {
                            await updateDoc(multaDocRef, {
                                pagada: true,
                                estado: 'saldada',
                                fechaPago: serverTimestamp(),
                                ingresoId: ingresoRef.id,
                                folioPago: folioString || null
                            });
                            console.log('✓ Se actualizó sanción administrativa a SALDADA en Firestore:', multaDocRef.id);
                        }

                        // 3. Descontar el cargo de la sanción del adeudo total de la casa en Firestore
                        const cargoSancion = Number(pago.montoNeto || pago.monto || multaTargetData?.monto || 0);
                        if (cargoSancion > 0) {
                            try {
                                const casaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', selectedCasa.id);
                                const casaSnap = await getDoc(casaDocRef);
                                if (casaSnap.exists()) {
                                    const cData = casaSnap.data() || {};
                                    const currentAdeudo = Number(cData.adeudo || 0);
                                    const currentCapital = Number(cData.adeudoCapital || 0);
                                    const currentMultas = Number(cData.adeudoMultas || cData.sancionesAdmin || 0);
                                    const newAdeudo = Math.max(0, Math.round((currentAdeudo - cargoSancion) * 100) / 100);
                                    const newCapital = Math.max(0, Math.round((currentCapital - cargoSancion) * 100) / 100);
                                    const newMultas = Math.max(0, Math.round((currentMultas - cargoSancion) * 100) / 100);

                                    await updateDoc(casaDocRef, {
                                        adeudo: newAdeudo,
                                        adeudoCapital: newCapital,
                                        adeudoMultas: newMultas,
                                        sancionesAdmin: newMultas
                                    });
                                    console.log(`✓ [GenerarTicket] Se descontaron $${cargoSancion} del adeudo de la casa por sanción. Nuevo adeudo: $${newAdeudo}`);
                                }
                            } catch (errCasaUpdate) {
                                console.error('Error al actualizar adeudo de la casa por sanción administrativa:', errCasaUpdate);
                            }
                        }
                    } catch (errMulta) {
                        console.warn('Error al marcar sanción como pagada en Firestore:', errMulta);
                    }
                }
            }

            // Cargar la configuración de recibos para enviar con los correos
            let recibosConfig = {};
            try {
                const fraccDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (fraccDoc.exists()) {
                    const data = fraccDoc.data() || {};
                    recibosConfig = data.configuracionRecibos || {};
                    // Fallback a legacy root-level fields
                    const rootConfig = {};
                    const allowed = [
                        'logoUrl', 'logoWidth', 'logoHeight',
                        'selloUrl', 'selloWidth', 'selloHeight',
                        'razonSocial', 'rfc', 'datosFacturacion',
                        'adminEmail', 'administradorNombre', 'adminDireccion',
                        'condominioNombre', 'condominioDireccion',
                        'folioSerie', 'folioConsecutivo', 'folioSerieEgreso', 'folioConsecutivoEgreso',
                        'moneda'
                    ];
                    allowed.forEach((k) => {
                        if (data[k] !== undefined) rootConfig[k] = data[k];
                    });
                    recibosConfig = { ...rootConfig, ...recibosConfig };
                }
            } catch (configErr) {
                console.error('GenerarTicket: error al cargar configuracionRecibos', configErr);
            }

            // Ahora enviar correos con datos completos de ingresos guardados (Offline-First)
            const emailDestino = selectedCasa?.email || selectedCasa?.correo || selectedCasa?.correoElectronico || selectedCasa?.correo_residente;
            if (emailDestino) {
                for (const { ref: ingresoRef, pago } of ingresoRefs) {
                    try {
                        let ingresoCompleto = null;
                        if (!navigator.onLine) {
                            const localSnap = await getDoc(ingresoRef);
                            ingresoCompleto = { id: localSnap.id, ...(localSnap.data() || {}) };
                        } else {
                            // Esperar a que el ingreso exista y tenga datos completos (procesado por Cloud Function)
                            for (let i = 0; i < 15; i++) {
                                await new Promise(resolve => setTimeout(resolve, 800)); // ~12 segundos total
                                try {
                                    const snap = await getDocFromServer(ingresoRef);
                                    if (snap.exists()) {
                                        const data = snap.data() || {};
                                        // onIngresoCreated escribe mesesAplicados o adeudoNuevo; esperar hasta que existan
                                        const procesado = data.mesesAplicados !== undefined || data.adeudoNuevo !== undefined || data.aplicadoAIntereses !== undefined;
                                        if (procesado || i >= 14) {
                                            ingresoCompleto = { id: snap.id, ...data };
                                            break;
                                        }
                                    }
                                } catch (e) {
                                    console.warn('GenerarTicket: error en getDocFromServer, reintentando...', e);
                                }
                            }
                        }

                        if (ingresoCompleto) {
                            const folioIndex = await computeFolioIndex(fraccionamientoId, ingresoRef.id);
                            const ingresoConFolio = { ...ingresoCompleto, folioIndex };

                            // Preparar datos completos de la casa incluyendo teléfono
                            const telefono = pickFirstString([
                                selectedCasa?.telefono,
                                selectedCasa?.telefono_residente,
                                selectedCasa?.tel,
                                selectedCasa?.celular,
                                selectedCasa?.telefonoPropietario,
                            ]);

                            const casaInfo = {
                                ...selectedCasa,
                                fraccionamientoId,
                                numero: selectedCasa.numero || selectedCasa.numeroCasa || selectedCasa.lote || '',
                                propietario: selectedCasa.propietario || selectedCasa.nombre || selectedCasa.nombre_residente || 'Residente',
                                nombre_residente: selectedCasa.nombre_residente || selectedCasa.nombre || selectedCasa.propietario || 'Residente',
                                telefono,
                                email: emailDestino,
                            };

                            const payloadToSend = {
                                email: emailDestino,
                                fraccId: fraccionamientoId,
                                receipt: ingresoConFolio,
                                config: recibosConfig,
                                casaInfo: { ...casaInfo, fraccionamientoId },
                            };

                            if (!navigator.onLine) {
                                pushToSyncQueue('sendReceiptEmail', payloadToSend);
                                console.log('GenerarTicket: Recibo encolado para envío offline.');
                                await updateDoc(ingresoRef, {
                                    emailEnviado: false,
                                    emailEnviadoOffline: true,
                                    emailEnviadoA: emailDestino,
                                });
                            } else {
                                const sendReceiptEmail = httpsCallable(functions, 'sendReceiptEmail');
                                const emailResp = await sendReceiptEmail(payloadToSend);
                                if (emailResp?.data?.success) {
                                    await updateDoc(ingresoRef, {
                                        emailEnviado: true,
                                        emailEnviadoEn: serverTimestamp(),
                                        emailEnviadoA: emailDestino,
                                    });
                                }
                            }
                        }
                    } catch (emailErr) {
                        console.error('GenerarTicket: error enviando recibo individual', emailErr);
                    }
                }
            }


            // Calcular valores para el registro de movimiento y UI
            const deudaActual = selectedCasa.adeudo || 0;
            const nuevaDeuda = Math.max(0, deudaActual - totalMonto);

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Pagos Recibidos - Casa ${selectedCasa.numero}`,
                    descripcion: `Se registraron ${pagos.length} pago(s) por un total de ${formatCurrency(totalMonto)} (descuento: ${formatCurrency(totalDescuento)}). Deuda estimada restante: ${formatCurrency(nuevaDeuda)}`,
                    categoria: 'Pagos',
                    tipoMovimiento: 'registro_pago',
                    origen: 'finanzas',
                    metadata: {
                        casaId: selectedCasa.id,
                        casaNumero: selectedCasa.numero,
                        cantidadPagos: pagos.length,
                        montoTotal: Number(totalMonto),
                        descuentoTotal: Number(totalDescuento),
                        montoNeto: Number(montoFinal),
                        deudaRestante: Number(nuevaDeuda),
                    },
                    userData,
                    currentUser,
                });
            } catch (registerError) {
                console.error('GenerarTicket: error al registrar el movimiento del pago', registerError);
            }

            // Mensaje de confirmación (la actualización real del saldo la hace la Cloud Function)
            const esMoroso = nuevaDeuda > 0;
            const estadoDeuda = esMoroso ? `Deuda estimada: ${formatCurrency(nuevaDeuda)}` : 'Deuda pagada completamente';
            setConfirmationModal({
                open: true,
                title: 'Pago Registrado',
                message: `Se ha registrado exitosamente ${pagos.length} pago(s) por ${formatCurrency(totalMonto)} para la casa ${selectedCasa.numero}. ${estadoDeuda}. El saldo se actualizará automáticamente.`,
                type: 'success',
            });

        } catch (error) {
            console.error("Error al registrar el pago: ", error);
            setConfirmationModal({
                open: true,
                title: 'Error',
                message: 'Hubo un error al procesar el pago. Intenta nuevamente.',
                type: 'error',
            });
        } finally {
            // El listener en tiempo real actualizará automáticamente las casas.
            // Se quitó handleCloseModal() de aquí por requerimiento para no cerrar el modal de cobro mientras se ve la ventana emergente de confirmación.
        }
    };

    const filteredCasas = casas.filter(casa => {
        if (!casa.numero) return false;
        const normSearch = searchTerm.toLowerCase().trim();
        const normNumero = casa.numero.toLowerCase().trim();
        return normNumero.includes(normSearch) || 
               `casa ${normNumero}`.includes(normSearch) ||
               normSearch.includes(normNumero);
    });

    // En src/pages/GenerarTicket.js

    // --- JSX DE RETORNO (COMPLETO) ---
    return (
        <div className="generar-ticket-page">
            <div className="generar-ticket-container">
                <header className="generar-ticket-header">
                    <div>
                        <h2>Generación de Tickets y Registro de Pagos</h2>
                        <p>Busca una casa para registrar su pago de mantenimiento.</p>
                    </div>
                    <button onClick={() => navigate('/administracion')} className="btn-regresar-admin">
                        <FaArrowLeft /> Regresar a administración
                    </button>
                </header>
            </div>

            <div className="search-bar">
                <div className="search-input-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Buscar por número de casa..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <span className="search-helper-text">
                    Filtra por el **número de casa** del residente.
                </span>
            </div>

            <div className="casas-list">
                {!loading && filteredCasas.length > 0 && (
                    <div className="casas-header">
                        <span>Casa (número de residencia)</span>
                        <span>Estado de cuenta actual</span>
                        <span>Nombre del residente</span>
                        <span>Adeudos</span>
                        <span>Acción</span>
                    </div>
                )}

                {loading ? (
                    <p>Cargando casas...</p>
                ) : filteredCasas.length === 0 ? (
                    <p className="no-results-text">No se encontraron casas con ese número.</p>
                ) : (
                    filteredCasas.map(casa => {
                        const residentName = getResidentName(casa);
                        const idNorm = String(casa.id || '').trim().toUpperCase();
                        const numNorm = String(casa.numero || casa.numeroCasa || '').trim().toUpperCase();
                        
                        const saldoFavor = Number(casa.saldoFavor || 0);
                        const adeudoAguaMap = (mapAguaPendiente[idNorm] || 0) || (mapAguaPendiente[numNorm] || 0);
                        const adeudoAgua = adeudoAguaMap || Number(casa.adeudoAgua || casa.montoAguaPendiente || 0);
                        const adeudoIntereses = Number(casa.adeudoInteres ?? casa.adeudoIntereses ?? casa.intereses ?? 0);
                        const adeudoMultas = (mapMultasPendientes[idNorm] || 0) || (mapMultasPendientes[numNorm] || 0);
                        const adeudoExtras = (mapExtrasPendientes[idNorm] || 0) || (mapExtrasPendientes[numNorm] || 0) || Number(casa.cuotasExtrasMonto ?? casa.adeudoExtras ?? 0);

                        // Mantenimiento ordinario / capital base
                        const adeudoCapital = Number(casa.adeudoCapital ?? casa.adeudo ?? casa.deuda ?? casa.montoAdeudo ?? 0);
                        const adeudoBaseNeto = saldoFavor > 0 ? 0 : adeudoCapital;

                        // Suma real de todos los cargos
                        const sumaAdeudos = adeudoBaseNeto + adeudoIntereses + adeudoAgua + adeudoMultas + adeudoExtras;
                        const adeudoNeto = Math.max(0, Math.round((sumaAdeudos - saldoFavor) * 100) / 100);

                        const saldoFavorLabel = saldoFavor > 0 ? `Saldo a favor: ${formatCurrency(saldoFavor)}` : null;
                        const adeudoInteresesLabel = adeudoIntereses > 0 ? formatCurrency(adeudoIntereses) : 'Sin intereses';

                        const cuotaMensual = Number(financialConfig.cuotaMensual || 0);
                        const mesesPermitidos = Number(financialConfig.mesesPermitidos || 1);
                        // Si el residente tiene saldo a favor, nunca puede ser moroso
                        const esMoroso = saldoFavor > 0 ? false : calcularEstadoMoroso(adeudoNeto, cuotaMensual, mesesPermitidos);

                        return (
                            <div key={casa.id} className="casa-item">
                                <div className="casa-inline">
                                    <div className="casa-col">
                                        <span className="casa-col-label">Casa</span>
                                        <span className="casa-numerico">Casa {casa.numero}</span>
                                    </div>

                                    <div className="casa-col">
                                        <span className="casa-col-label">Estado</span>
                                        <span className={`casa-status ${esMoroso ? 'moroso' : 'al-corriente'}`}>
                                            {esMoroso ? 'Con adeudo' : 'Al corriente'}
                                        </span>
                                    </div>

                                    <div className="casa-col">
                                        <span className="casa-col-label">Residente</span>
                                        <span className="casa-residente">{residentName || 'Residente no registrado'}</span>
                                    </div>

                                    <div className="casa-col">
                                        <div className="casa-adeudos-stack">
                                            <div className="casa-adeudo-item">
                                                <span className="casa-col-label">Adeudo Intereses</span>
                                                <span className={`casa-adeudo ${adeudoIntereses > 0 ? 'adeudo-pendiente' : 'adeudo-cero'}`}>
                                                    {adeudoInteresesLabel}
                                                </span>
                                            </div>
                                            <div className="casa-adeudo-item">
                                                <span className="casa-col-label">Adeudo Total</span>
                                                <div className="casa-adeudo-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    {saldoFavorLabel && (
                                                        <span className="saldo-favor-pill">{saldoFavorLabel}</span>
                                                    )}
                                                    {adeudoBaseNeto > 0 && (
                                                        <span className="casa-adeudo adeudo-pendiente">
                                                            Mantenimiento: {formatCurrency(adeudoBaseNeto)}
                                                        </span>
                                                    )}
                                                    {adeudoExtras > 0 && (
                                                        <span className="casa-adeudo adeudo-pendiente" style={{ color: '#d97706', backgroundColor: '#fef3c7', borderColor: '#fde68a', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                            Extras: {formatCurrency(adeudoExtras)}
                                                        </span>
                                                    )}
                                                    {adeudoMultas > 0 && (
                                                        <span className="casa-adeudo adeudo-pendiente" style={{ color: '#be185d', backgroundColor: '#fce7f3', borderColor: '#fbcfe8', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                            Sanciones: {formatCurrency(adeudoMultas)}
                                                        </span>
                                                    )}
                                                    {adeudoAgua > 0 && (
                                                        <span className="casa-adeudo adeudo-pendiente" style={{ color: '#0284c7', backgroundColor: '#e0f2fe', borderColor: '#b3e0ff', padding: '2px 6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 'bold' }}>
                                                            <FaTint /> Agua: {formatCurrency(adeudoAgua)}
                                                        </span>
                                                    )}
                                                    {adeudoNeto > 0 && (adeudoExtras > 0 || adeudoMultas > 0 || adeudoAgua > 0 || adeudoIntereses > 0) && (
                                                        <span className="casa-adeudo adeudo-pendiente" style={{ color: '#b91c1c', backgroundColor: '#fee2e2', borderColor: '#fca5a5', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                            Total: {formatCurrency(adeudoNeto)}
                                                        </span>
                                                    )}
                                                    {adeudoNeto === 0 && saldoFavor === 0 && (
                                                        <span className="casa-adeudo adeudo-cero">
                                                            Sin adeudo
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleOpenModal(casa)}
                                    className="btn-pagar"
                                    disabled={!isOnline}
                                    title={!isOnline ? "Conexión requerida para registrar pago" : ""}
                                    style={!isOnline ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                >
                                    Registrar pago
                                </button>
                            </div>
                        );
                    })
                )}
            </div>

            {isModalOpen && (
                <ModalRegistroPago
                    casa={selectedCasa}
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmPago}
                />
            )}

            <ConfirmationModal
                open={confirmationModal.open}
                title={confirmationModal.title}
                message={confirmationModal.message}
                type={confirmationModal.type}
                icon={confirmationModal.type === 'success' ? <FaCheckCircle /> : undefined}
                actionLabel="Aceptar"
                onClose={() => {
                    setConfirmationModal({ ...confirmationModal, open: false });
                    if (confirmationModal.type === 'success') {
                        handleCloseModal();
                    }
                }}
                onAction={() => {
                    if (confirmationModal.type === 'success') {
                        handleCloseModal();
                    }
                }}
            />
        </div>
    );
};
export default GenerarTicket;